/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import styles from "gui/parameters/parameters.module.css";
import { Datepicker } from "gui/datepicker/datepicker";
import { Icon } from "gui/icon/icon";
import { Parameter, ParameterMerge } from "lib/parameters/types";
import { ParameterDialogButton } from "gui/parameters/dialog";
import { findParameter, uniqueParam } from "lib/parameters/parameters";
import { logger } from "lib/logger/browser";
import { preventDefault } from "lib/events/events";
import cn from "classnames";
import { TooltipOld } from "components/TooltipOld/TooltipOld";
import { Tooltip } from "components/Tooltip/Tooltip";
import * as Menu from "components/MenuPanel/MenuPanel";
import { useDebouncedEffect } from "lib/hooks/use-debounced-effect";

interface Props {
  parameters: Parameter[];
  overrides: Parameter[];
  applied: Parameter[];
  onOverride?: (parameter: Parameter) => void;
  onApply?: () => void;
  onRemove?: (parameter: Parameter) => void;
  onReplace?: (index: number, parameter: Parameter) => void;
  disabled?: boolean;
  canEdit?: boolean;
}

// Used on query page
export const Parameters: React.FC<Props> = (props) => {
  if (props.parameters.length === 0) {
    return null;
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (document.activeElement === event.target && event.key === "Enter") {
      props.onApply?.();
    }
  };

  return (
    <form onSubmit={preventDefault} className={styles.parameters}>
      {props.parameters.map((p, i) => (
        <TooltipOld
          key={uniqueParam(p)}
          label="Sign in or sign up to use parameters."
          color="black"
          hidden={props.canEdit || !props.disabled}
        >
          <label
            className={cn(styles.parameter, props.disabled && styles.disabled)}
          >
            <span className={styles.label}>{p.key}</span>
            <OverrideInput
              parameter={p}
              override={findParameter(props.overrides, uniqueParam(p))}
              onChange={props.onOverride}
              onKeyDown={onKeyDown}
              onApply={props.onApply}
              disabled={props.disabled}
            />
            {props.onReplace && (
              <ParameterDialogButton
                {...props}
                parameter={p}
                onReplace={props.onReplace.bind(null, i)}
              />
            )}
          </label>
        </TooltipOld>
      ))}
    </form>
  );
};

// Used on dashboard page
export const Param: React.FC<{
  parameter: Parameter;
  parameters: ParameterMerge;
  onApply?: () => void;
  openRunModal?: () => void;
  disabled?: boolean;
  canEdit?: boolean;
}> = (props) => {
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (document.activeElement === event.target && event.key === "Enter") {
      props.onApply?.();
      // `setTimeout(fn, 0)`: Open the modal after the Enter click is
      // done propagating, otherwise the modal closes immediately.
      props.openRunModal && setTimeout(props.openRunModal, 0);
    }
  };

  const parameterElement = (
    <div
      className={cn(styles.parameter, props.disabled && styles.disabled)}
      key={uniqueParam(props.parameter)}
    >
      <span className={styles.label}>{props.parameter.key}</span>
      <OverrideInput
        parameter={props.parameter}
        override={findParameter(
          props.parameters.state.overrides,
          uniqueParam(props.parameter)
        )}
        onChange={props.parameters.state.onOverride}
        onKeyDown={onKeyDown}
        onApply={props.onApply}
        disabled={props.disabled}
      />
    </div>
  );

  if (props.disabled || props.canEdit) {
    return (
      <TooltipOld label="Sign in or sign up to use parameters." color="black">
        {parameterElement}
      </TooltipOld>
    );
  }

  return (
    <Tooltip
      position="above-center"
      label={
        <>
          To run, hit <Menu.Shortcut shortcut="enter" /> while focused
        </>
      }
    >
      {parameterElement}
    </Tooltip>
  );
};

const OverrideInput: React.FC<{
  parameter: Parameter;
  override?: Parameter;
  onChange?: (parameter: Parameter) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onApply?: () => void;
  disabled?: boolean;
}> = (props) => {
  const value = props.override?.value ?? "";

  useDebouncedEffect(
    { delayMs: 500 },
    props.disabled ? () => {} : () => props.onApply?.(),
    [value]
  );

  const onString = (value: string) => {
    props.onChange?.({
      ...(props.override ?? props.parameter),
      value,
    });
  };

  const onNumber = (value: string) => {
    props.onChange?.({
      ...(props.override ?? props.parameter),
      value: value.replace(/[^0-9.]/g, ""),
    });
  };

  if (props.parameter.type === "text") {
    return (
      <input
        placeholder={props.parameter.value}
        value={value}
        onChange={(event) => onString(event.target.value)}
        disabled={props.disabled || !props.onChange}
        onKeyDown={props.onKeyDown}
      />
    );
  }

  if (props.parameter.type === "number") {
    return (
      <input
        placeholder={props.parameter.value}
        value={value}
        onChange={(event) => onNumber(event.target.value)}
        disabled={props.disabled || !props.onChange}
        onKeyDown={props.onKeyDown}
      />
    );
  }

  if (props.parameter.type === "datetime") {
    return (
      <Datepicker
        disabled={props.disabled || !props.onChange}
        kind="parameter"
        onChange={onString}
        placeholder={props.parameter.value}
        required={false}
        value={value}
        onKeyDown={props.onKeyDown}
      />
    );
  }

  if (props.parameter.type === "enum") {
    return (
      <div className={styles.select}>
        <select
          value={value || props.parameter.value}
          disabled={
            props.disabled ||
            !props.onChange ||
            !props.parameter.enumOptions?.length
          }
          onChange={(event) => onString(event.target.value)}
          onKeyDown={props.onKeyDown}
        >
          {props.parameter.enumOptions?.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <Icon icon="caret-down-fill" aria-hidden />
      </div>
    );
  }

  logger.warn("unsupported parameter type", props.parameter["type"]);
  return null;
};
